/*
 * Copyright (c) 2021 - 2026 All rights reserved by Ferenc Farkas <wolferi93@gmail.com>
 */

/**
 * @file geoPin.js
 * @description
 * Pure helpers for the "exact location" pin that the CRM customer boxes and the
 * Driver app share: coordinate validation, the backend `GeoPin` shape, display
 * formatting and the navigation-destination override. No React and no native
 * module is imported, so every screen (and Jest) can use it.
 *
 * `GeoPin` (backend contract): `{lat, lng, accuracy, source, updatedAt, updatedBy}`
 * - `lat` -90..90 and `lng` -180..180, `accuracy` in metres or `null`;
 * - `source` is `admin` | `app` | `driver` | `geocode`; `updatedAt` / `updatedBy`
 *   are stamped by the server and only ever read here.
 *
 * @module helpers/geoPin
 */

/** Sources the backend stamps on a pin. */
export const PIN_SOURCES = Object.freeze(['admin', 'app', 'driver', 'geocode']);

/** Camera fallback when there is neither a pin nor an address (Budapest, like the fleet maps). */
export const DEFAULT_PIN_CENTER = Object.freeze({lat: 47.4979, lng: 19.0402});

/**
 * Parse a number out of a number or a numeric string; booleans, blanks and
 * `null` are never coordinates.
 * @param {*} value Raw value.
 * @returns {number} The number, `NaN` when it is not one.
 */
const toNumber = (value) => {
    if (typeof value === 'number') {
        return value;
    }
    if (typeof value === 'string' && value.trim() !== '') {
        return Number(value.trim());
    }

    return NaN;
};

/**
 * Round to 6 decimals (about 0.1 m) so equal places compare equal.
 * @param {number} value Coordinate.
 * @returns {number} Rounded coordinate.
 */
const round6 = (value) => Math.round(value * 1e6) / 1e6;

/**
 * Whether a latitude / longitude pair can be stored as a pin. `0, 0` is rejected
 * on purpose: it is what an empty GPS fix or an unset field looks like.
 * @param {*} lat Latitude.
 * @param {*} lng Longitude.
 * @returns {boolean} True for finite, in-range, non-origin coordinates.
 */
export const isValidCoordinate = (lat, lng) => {
    const la = toNumber(lat);
    const ln = toNumber(lng);

    if (!Number.isFinite(la) || !Number.isFinite(ln)) {
        return false;
    }
    if (la < -90 || la > 90 || ln < -180 || ln > 180) {
        return false;
    }

    return !(la === 0 && ln === 0);
};

/**
 * Normalise anything pin-like (`{lat, lng}`, `{latitude, longitude}` or a stored
 * `GeoPin`) to a full `GeoPin`.
 * @param {*} raw Pin-like value.
 * @returns {?{lat: number, lng: number, accuracy: ?number, source: string, updatedAt: string, updatedBy: ?string}}
 *   The pin, `null` when the coordinates are missing or invalid.
 */
export const normalizePin = (raw) => {
    if (!raw || typeof raw !== 'object') {
        return null;
    }

    const lat = toNumber(raw.lat ?? raw.latitude);
    const lng = toNumber(raw.lng ?? raw.lon ?? raw.longitude);

    if (!isValidCoordinate(lat, lng)) {
        return null;
    }

    const accuracy = toNumber(raw.accuracy);

    return {
        lat: round6(lat),
        lng: round6(lng),
        accuracy: Number.isFinite(accuracy) && accuracy >= 0 ? Math.round(accuracy) : null,
        source: PIN_SOURCES.includes(raw.source) ? raw.source : '',
        updatedAt: typeof raw.updatedAt === 'string' ? raw.updatedAt : '',
        updatedBy: typeof raw.updatedBy === 'string' && raw.updatedBy ? raw.updatedBy : null,
    };
};

/**
 * Body fragment the write endpoints take for a pin (`lat`, `lng`, optional
 * `accuracy` and `source`). The server stamps `updatedAt` / `updatedBy` itself.
 * @param {*} pin Pin-like value.
 * @param {string} [source] Client source to send (`app` in the app; the driver endpoint sets its own).
 * @returns {?{lat: number, lng: number, accuracy?: number, source?: string}} Payload, `null` for an invalid pin.
 */
export const pinToPayload = (pin, source) => {
    const normalized = normalizePin(pin);

    if (!normalized) {
        return null;
    }

    return {
        lat: normalized.lat,
        lng: normalized.lng,
        ...(normalized.accuracy !== null ? {accuracy: normalized.accuracy} : {}),
        ...(source ? {source} : {}),
    };
};

/**
 * Whether two pins mark the same place (6-decimal precision). Two missing pins are equal.
 * @param {*} a First pin-like value.
 * @param {*} b Second pin-like value.
 * @returns {boolean} True when both are missing or both have the same coordinates.
 */
export const pinsEqual = (a, b) => {
    const left = normalizePin(a);
    const right = normalizePin(b);

    if (!left || !right) {
        return !left && !right;
    }

    return left.lat === right.lat && left.lng === right.lng;
};

/**
 * `47.497900, 19.040200` — the coordinates as one line.
 * @param {*} pin Pin-like value.
 * @returns {string} The line, `''` for an invalid pin.
 */
export const formatCoordinates = (pin) => {
    const normalized = normalizePin(pin);

    return normalized ? `${normalized.lat.toFixed(6)}, ${normalized.lng.toFixed(6)}` : '';
};

/**
 * Who set the pin, folded to the three audiences the UI words differently:
 * a driver in the field, the office (web admin / CRM app) or the address search.
 * @param {*} pin Pin-like value.
 * @returns {('driver'|'office'|'geocode'|'')} Kind, `''` when the source is unknown.
 */
export const pinSourceKind = (pin) => {
    switch (normalizePin(pin)?.source) {
    case 'driver':
        return 'driver';
    case 'admin':
    case 'app':
        return 'office';
    case 'geocode':
        return 'geocode';
    default:
        return '';
    }
};

/**
 * Navigation destination that prefers the pin over the (less exact) address:
 * the address text stays as the label / fallback, the coordinates win.
 * @param {{address?: string, lat?: number, lng?: number, label?: string}} destination Destination as `navigateTo` takes it.
 * @param {*} pin Pin-like value.
 * @returns {object} The destination with the pin's coordinates, or the input when there is no valid pin.
 */
export const withPinDestination = (destination, pin) => {
    const normalized = normalizePin(pin);

    if (!normalized) {
        return destination;
    }

    return {...(destination || {}), lat: normalized.lat, lng: normalized.lng};
};

/**
 * `react-native-maps` region centred on a point.
 * @param {{lat: number, lng: number}} point Centre.
 * @param {number} [delta=0.004] Latitude / longitude span in degrees (smaller = closer).
 * @returns {{latitude: number, longitude: number, latitudeDelta: number, longitudeDelta: number}} Region.
 */
export const regionForPoint = (point, delta = 0.004) => ({
    latitude: point.lat,
    longitude: point.lng,
    latitudeDelta: delta,
    longitudeDelta: delta,
});
